import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import strings from '../../constant/strings';
import { resendPreorderOtpApi } from '../../services/authService';
import { preorderOtpLoginAction } from '../../redux/auth/authActions';
import { Card, Muted, Notice, OutlineButton, PrimaryButton, SectionTitle } from './styles';

/**
 * The code that confirms a pre-order signup.
 *
 * Typed here rather than clicked in the email, because a link opens in
 * whatever browser the mail app chooses - a different session from the one the
 * patient was buying in, leaving their order stranded in another window. A
 * code comes back to the tab they are already standing in.
 *
 * Confirming also signs them in, so there is no password step after this.
 */
const Wrap = styled.div`
  max-width: 460px;
  margin: 0 auto;
  padding: 0 16px 48px;
`;

const CodeInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
  font-size: 26px;
  letter-spacing: 10px;
  text-align: center;
  padding: 14px 12px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p['data-invalid'] === 'true' ? '#D98282' : '#E4E4ED')};
  color: #2B2B2B;
  outline: none;

  &:focus { border-color: #1AA19C; }

  /* The spinners are meaningless on a code and make it easy to misfire. */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
`;

const COOLDOWN_SECONDS = 60;

export default function PreorderOtpForm({ email, onBack }) {
  const dispatch = useDispatch();
  const error = useSelector((state) => state.auth.error);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(COOLDOWN_SECONDS);
  const inputRef = useRef(null);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  // Counts down so the resend link says when it will work, rather than
  // failing with a cooldown error when pressed.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // The saga reports failure through the auth store; releasing the button here
  // lets them correct a wrong code and try again.
  useEffect(() => { if (error) setBusy(false); }, [error]);

  const submit = (e) => {
    if (e) e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    dispatch(preorderOtpLoginAction({ email, otp: code }));
  };

  const resend = async () => {
    setResending(true);
    const res = await resendPreorderOtpApi({ email });
    setResending(false);
    if (res.response_type === 'fail') {
      toast.error(res.response?.message || strings.preorderGenericError, {
        position: 'bottom-center', closeButton: false, autoClose: 3000,
        style: { width: 'auto', borderRadius: 8 },
      });
      return;
    }
    setCode('');
    setCooldown(COOLDOWN_SECONDS);
    toast.success(strings.codeResent, {
      position: 'bottom-center', closeButton: false, autoClose: 2500,
      style: { width: 'auto', borderRadius: 8 },
    });
  };

  return (
    <Wrap>
      <Card>
        <SectionTitle>{strings.enterTheCode}</SectionTitle>
        <Muted style={{ marginBottom: 18 }}>
          {strings.codeSentTo} <strong style={{ color: '#2B2B2B' }}>{email}</strong>.
          {' '}{strings.codeExpiryNote}
        </Muted>

        <form onSubmit={submit}>
          <CodeInput
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="------"
            aria-label={strings.enterTheCode}
            data-invalid={error ? 'true' : 'false'}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />

          {error ? <Notice data-tone="error">{error}</Notice> : null}

          <PrimaryButton
            type="submit"
            fullWidth
            disabled={code.length !== 6 || busy}
            style={{ marginTop: 16 }}
          >
            {busy ? strings.loading : strings.confirmAndContinue}
          </PrimaryButton>
        </form>

        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {cooldown > 0 ? (
            <Muted>{strings.resendIn} {cooldown}s</Muted>
          ) : (
            <OutlineButton size="small" onClick={resend} disabled={resending}>
              {resending ? strings.loading : strings.resendCode}
            </OutlineButton>
          )}
          {onBack ? (
            <OutlineButton size="small" onClick={onBack} style={{ marginLeft: 'auto' }}>
              {strings.useAnotherEmail}
            </OutlineButton>
          ) : null}
        </div>
      </Card>
    </Wrap>
  );
}
